import React, { useCallback, type RefCallback, memo, useId as useReactId, useMemo } from 'react';
import { useUID } from 'react-uid';
import type { TriggerProps } from '@atlaskit/popup';
import { Section } from '@atlaskit/menu';
import { Settings as NewSettingsButton } from '@atlaskit/navigation-system/top-nav-items/settings';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { useTopMenus } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import {
	testIdConcat,
	testIdGenerate,
} from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	EXPERIENCE_NAVIGATION_TOP_MENU,
	NAVIGATION_ITEM_ID,
	PACKAGE_NAME,
} from '../../common/constants.tsx';
import { useNavigationItemAnalytics } from '../../controllers/navigation-item-analytics/main.tsx';
import { MenuLayout } from '../../common/ui/menu/layout/index.tsx';
import { MenuSkeletonContent } from '../../common/ui/menu/skeleton/content/main.tsx';
import { MenuSkeletonFooter } from '../../common/ui/menu/skeleton/footer/main.tsx';
import { TopLevelErrorBoundary } from '../../common/ui/error-boundary/index.tsx';
import { settingsMenuEntryPoint } from './menu/entrypoint.tsx';
import messages from './menu/messages.tsx';

const useId = functionWithCondition(
	() => fg('jira-react-uid-to-use-id-migration'),
	useReactId,
	useUID,
);

export const Settings = memo(() => {
	return (
		<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.SETTINGS}>
			<SettingsContainer />
		</TopLevelErrorBoundary>
	);
});

function SettingsContainer() {
	const isAnonymous = useIsAnonymous();

	if (isAnonymous) return null;

	return <SettingsInner />;
}

function SettingsInner() {
	const isAdmin = useIsAdmin();
	const cloudId = useCloudId();
	const appEditions = useAppEditions();
	const { formatMessage } = useIntl();

	const [isOpen, { toggle: toggleMenu, off: closeMenu }] = useTopMenus(MENU_ID.SETTINGS);

	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.SETTINGS,
	});

	useExperienceStart({
		experience: EXPERIENCE_NAVIGATION_TOP_MENU,
		experienceId: NAVIGATION_ITEM_ID.SETTINGS,
		analyticsSource: 'atlassian-navigation',
	});

	const onClick = useCallback(() => {
		toggleMenu();
		triggerAnalytics({ shownCommandPalettePrompt: isAdmin });
	}, [isAdmin, toggleMenu, triggerAnalytics]);

	const testIdPrefix = testIdGenerate('secondary-actions', 'settings');

	const id = useId();
	const titleId = `top-navigation-settings-header-${id}-title`;

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			isAdmin,
			appEditions,
		}),
		[cloudId, isAdmin, appEditions],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		settingsMenuEntryPoint,
		entryPointParams,
	);
	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	const Trigger = useCallback(
		(triggerProps: Partial<TriggerProps>) => (
			<div
				data-testid={testIdConcat(testIdPrefix, 'menu-trigger')}
				data-vc="atlassian-navigation-settings"
			>
				<NewSettingsButton
					{...triggerProps}
					ref={(element) => {
						triggerRef(element);
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(triggerProps.ref as RefCallback<HTMLElement>)?.(element);
					}}
					onClick={onClick}
					isSelected={isOpen}
					interactionName="nav4-topNav-settings"
					label={formatMessage(messages.settings)}
				/>
			</div>
		),
		[formatMessage, isOpen, onClick, testIdPrefix, triggerRef],
	);

	if (__SERVER__) return <Trigger aria-expanded={false} aria-haspopup />;

	return (
		<Popup
			titleId={titleId}
			isOpen={isOpen}
			content={() => (
				<JiraEntryPointContainer
					id="nav4-topNav-settings"
					entryPointReferenceSubject={entryPointReferenceSubject}
					packageName={PACKAGE_NAME}
					errorFallback="flag"
					fallback={
						<MenuLayout isWide>
							<Section isScrollable>
								<MenuSkeletonContent testIdPrefix={testIdPrefix} />
							</Section>
							<Section hasSeparator>
								<MenuSkeletonFooter testIdPrefix={testIdPrefix} />
							</Section>
						</MenuLayout>
					}
					runtimeProps={{ testIdPrefix, titleId }}
				/>
			)}
			onClose={closeMenu}
			placement="bottom-end"
			messageId="atlassian-navigation.ui.settings.popup"
			messageType="transactional"
			trigger={Trigger}
			label={formatMessage(messages.settings)}
			shouldRenderToParent
		/>
	);
}
